import { Icon } from '@components/index';
import CustomTooltip from '@components/tooltip/Tooltip';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import useIsTruncated from '@hooks/useIsTruncated';
import { formatISODate } from '@utils/date';

import { LeadSource } from '../constants';
import useCanEditLead from '../hooks/useCanEditLead';
import useLeadSources from '../hooks/useLeadSources';
import { LeadType } from '../types/common';
import { formatMobileNumber } from '../utils/common';

interface LeadInformationProps {
  handleEditContact: () => void;
  leadData: LeadType;
}

const LeadInformation = ({
  handleEditContact,
  leadData
}: LeadInformationProps) => {
  const { ref: adNameRef, isTruncated } = useIsTruncated<HTMLSpanElement>(
    leadData?.ad?.name
  );
  const { getSourceLabel } = useLeadSources();
  const canEditLead = useCanEditLead();
  const contactInfo = [
    {
      icon: 'phone',
      label: 'Mobile Number',
      value: formatMobileNumber(
        leadData?.mobile_number,
        leadData?.country_code
      ),
      hasWhatsApp: true
    },
    {
      icon: 'phone',
      label: 'Alternate Number',
      value: leadData?.secondary_mobile_number
        ? formatMobileNumber(
            leadData?.secondary_mobile_number,
            leadData?.secondary_country_code
          )
        : '',
      hasWhatsApp: false
    },
    {
      icon: 'mail',
      label: 'Email ID',
      value: leadData?.email_id
    }
  ];

  const moreInfo = [
    {
      icon: 'location',
      label: 'Location',
      value:
        leadData?.city || leadData?.state
          ? `${leadData?.city || ''}${
              leadData?.city && leadData?.state ? ', ' : ''
            }${leadData?.state || ''}`.trim()
          : ''
    },
    {
      icon: 'clock',
      label: leadData?.is_external_synced
        ? 'Created time (in meta)'
        : 'Created Time',
      value: leadData?.created_at
        ? formatISODate({
            date: leadData.created_at,
            format: 'DD MMM YYYY hh:mm am/pm'
          })
        : ''
    },
    {
      icon: 'ruppee-circle',
      label: 'Deal Value',
      value: (
        <>
          <span className="font-inter">₹ </span>
          {((leadData?.deal_value ?? 0) / 100)?.toLocaleString()}
        </>
      )
    },
    {
      icon: 'category-icon',
      label: 'Lead Category',
      value: leadData?.category?.value
    },
    {
      icon: 'source-icon',
      label: 'Source',
      value:
        leadData?.source === LeadSource.FACEBOOK_ADS && leadData?.ad?.name ? (
          <>
            <span
              ref={adNameRef}
              className="line-clamp-3"
              data-tooltip-id="source-ad-tooltip"
            >
              {`Facebook Ad: ${leadData.ad.name}`}
            </span>
            {isTruncated && (
              <CustomTooltip
                id="source-ad-tooltip"
                type="text"
                tooltipText={`Facebook Ad: ${leadData.ad.name}`}
                place="top"
              />
            )}
          </>
        ) : (
          getSourceLabel(leadData?.source)
        )
    },
    {
      icon: 'open-book',
      label: 'Description',
      value: leadData?.description
    }
  ];

  return (
    <div className="flex w-[347px] flex-col gap-4 self-start">
      <div className="rounded-xl bg-white p-6 shadow-sm">
        <div className="flex flex-row justify-between">
          <h2 className="mb-2 text-18 font-semibold text-content">
            Lead Details
          </h2>
          {canEditLead && (
            <Icon
              name="edit-grey"
              size="medium"
              className="cursor-pointer text-gray-600 hover:text-gray-800"
              style={{ width: '24px', height: '24px' }}
              onClick={handleEditContact}
            />
          )}
        </div>

        <h2 className="mb-4 text-base font-semibold text-gray-900">
          Contact Information
        </h2>
        <div className="space-y-4">
          {contactInfo.map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <div className="flex min-w-0 gap-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
                  <Icon
                    name={item.icon}
                    size="small"
                    className="text-gray-600"
                    style={{ width: '18px', height: '18px' }}
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium text-grey">
                    {item.label}
                  </span>
                  <span className="break-words text-base font-medium text-content">
                    {item?.value ? item?.value : DEFAULT_PLACEHOLDER}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <h2 className="my-4 text-base font-semibold text-gray-900">
          More Info
        </h2>
        <div className="space-y-4">
          {moreInfo.map((item) => (
            <div key={`${item.icon}-${item.label}`} className="flex gap-3">
              <div className="flex size-10 min-h-[40px] min-w-[40px] items-center justify-center rounded-full bg-gray-100">
                <Icon
                  name={item.icon}
                  size="medium"
                  className="text-gray-600"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-grey">
                  {item.label}
                </span>
                <span className="break-words text-base font-medium text-content">
                  {item?.value ? item?.value : DEFAULT_PLACEHOLDER}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadInformation;
